import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './layout/components/App.tsx';

import './common/i18n/config';

import { Provider } from 'react-redux';
import store from './features/ui/store.tsx';
import { AuthProvider } from './common/providers/AuthProvider.tsx';
import { ApiClientProvider } from './common/providers/ApiClientProvider.tsx';
import { UserStorageProvider } from './common/providers/UserStorageProvider.tsx';
import { ErrorBoundary } from 'react-error-boundary';
import { CriticalFailure } from '@cloudflare-monorepo/ui/components/CriticalFailure.tsx';
import { CachedDataProvider } from './common/providers/CachedDataProvider.tsx';
import * as Sentry from '@sentry/react';
import { ManagedError } from '@cloudflare-monorepo/utils/errors';
import { GlobalLogger } from './common/services/logging.ts';
import { ModalProvider } from '@cloudflare-monorepo/ui/providers/ModalProvider.tsx';
import { ToastProvider } from '@cloudflare-monorepo/ui/providers/ToastProvider.tsx';
import { QueryProvider } from '@cloudflare-monorepo/ui/providers/QueryProvider.tsx';
import { LoggerProvider } from '@cloudflare-monorepo/ui/providers/LoggerProvider.tsx';
import { AnyResponseError } from './common/providers/ApiClientProvider.tsx';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV_NAME || 'unknown',

    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,

    integrations: [
      Sentry.httpContextIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],

    beforeSend(event, hint) {
      const originalException = hint?.originalException;

      if (originalException) {
        const errStatus = (originalException as AnyResponseError).response?.status;
        if (errStatus && errStatus >= 400 && errStatus < 500) {
          // do not report HTTP 4xx errors
          return null;
        }
      }

      if (originalException instanceof ManagedError) {
        event.extra = {
          ...event.extra,
          code: originalException.code,
          details: originalException.details,
        };
        event.tags = {
          ...event.tags,
          customType: 'ManagedError',
        };
      }

      return event;
    },
  });
}

window.addEventListener('vite:preloadError', () => {
  GlobalLogger.warn('vite:preloadError triggered, hard reloading the page');
  window.location.reload(); // for example, refresh the page
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'FORCE_REFRESH') {
      window.location.reload();
    }
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary
      onError={(err, info) => {
        if (import.meta.env.VITE_SENTRY_DSN) {
          Sentry.captureReactException(err, info);
        }
      }}
      fallback={<CriticalFailure />}
    >
      <LoggerProvider logger={GlobalLogger}>
        <AuthProvider>
          <UserStorageProvider>
            <ApiClientProvider>
              <CachedDataProvider>
                <QueryProvider>
                  <Provider store={store}>
                    <ModalProvider>
                      <ToastProvider>
                        <App />
                      </ToastProvider>
                    </ModalProvider>
                  </Provider>
                </QueryProvider>
              </CachedDataProvider>
            </ApiClientProvider>
          </UserStorageProvider>
        </AuthProvider>
      </LoggerProvider>
    </ErrorBoundary>
  </StrictMode>,
);
